import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { rfwNetworkConfig } from '@/mocks/rfwNetwork';
import type { RfwNetworkConfig, RfwSpaceConfig, RfwHistoryEntry } from '@/mocks/rfwNetwork';

type LoadState = 'loading' | 'ready' | 'error';

function simulateLoad<T>(data: T): Promise<T> {
  return new Promise((resolve, reject) => {
    const d = 350 + Math.random() * 200;
    setTimeout(() => {
      if (Math.random() > 0.97) {
        reject(new Error('Timeout al conectar con el servidor.'));
      } else {
        resolve(data);
      }
    }, d);
  });
}

function simulateSave(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(() => resolve(), 600 + Math.random() * 400);
  });
}

function formatCurrency(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString()}`;
}

function formatCurrencyFull(n: number): string {
  return `$${n.toLocaleString('es-AR')}`;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

/* ── Loading Skeleton ── */
function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="bg-background-50 border border-background-200 rounded-2xl p-6 animate-pulse">
        <div className="w-40 h-4 bg-background-200 rounded mb-3"></div>
        <div className="w-28 h-8 bg-background-200 rounded mb-4"></div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-14 bg-background-200 rounded-xl"></div>
          ))}
        </div>
      </div>
      <div className="bg-background-50 border border-background-200 rounded-2xl p-5 animate-pulse">
        <div className="w-36 h-4 bg-background-200 rounded mb-4"></div>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-14 bg-background-200 rounded-xl mb-2"></div>
        ))}
      </div>
    </div>
  );
}

/* ── Error State ── */
function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-rose-50 flex items-center justify-center">
        <i className="ri-error-warning-line text-rose-400 text-2xl"></i>
      </div>
      <p className="text-foreground-800 text-sm font-semibold mb-1">Error al cargar</p>
      <p className="text-foreground-400 text-xs mb-4">{message}</p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
      >
        <i className="ri-refresh-line text-sm"></i>
        Reintentar
      </button>
    </div>
  );
}

/* ── Participation Toggle ── */
function ParticipationToggle({ active, onChange, saving }: { active: boolean; onChange: (v: boolean) => void; saving: boolean }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
            <i className="ri-settings-3-line text-amber-600 text-lg"></i>
          </div>
          <div>
            <h3 className="text-foreground-800 text-sm font-bold">Configurar participación</h3>
            <p className="text-foreground-400 text-[10px] mt-0.5">
              Activá o desactivá tu participación en la Red Publicitaria RFW. Cuando está activa, RFW puede colocar anunciantes en los espacios que elijas.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => onChange(false)}
            disabled={saving}
            className={`px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
              !active
                ? 'bg-foreground-900 text-background-50'
                : 'bg-background-100 text-foreground-400 hover:bg-background-200 hover:text-foreground-600'
            }`}
          >
            <i className="ri-close-circle-line mr-1"></i>
            No
          </button>
          <button
            onClick={() => onChange(true)}
            disabled={saving}
            className={`px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
              active
                ? 'bg-amber-500 text-white'
                : 'bg-background-100 text-foreground-400 hover:bg-background-200 hover:text-foreground-600'
            }`}
          >
            <i className="ri-checkbox-circle-line mr-1"></i>
            Sí
          </button>
        </div>
      </div>

      {active && (
        <div className="mt-4 pt-4 border-t border-background-200 flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-bold">
            <span className="w-2 h-2 rounded-full bg-amber-500"></span>
            Participando activamente
          </div>
          <span className="text-foreground-400 text-[10px]">RFW está colocando anunciantes en tus espacios habilitados</span>
        </div>
      )}
    </div>
  );
}

/* ── Space Checkbox Card ── */
function SpaceCard({
  space,
  disabled,
  onToggle,
}: {
  space: RfwSpaceConfig;
  disabled: boolean;
  onToggle: (key: string) => void;
}) {
  return (
    <div
      className={`bg-background-50 border rounded-2xl p-4 transition-all ${
        space.enabled && !disabled ? 'border-amber-200 bg-amber-50/30' : 'border-background-200'
      } ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <button
            onClick={() => onToggle(space.key)}
            className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border-2 transition-all cursor-pointer ${
              space.enabled && !disabled
                ? 'border-amber-500 bg-amber-500 text-white'
                : 'border-background-300 bg-background-50 text-background-300'
            }`}
          >
            {space.enabled && !disabled ? (
              <i className="ri-check-line text-base"></i>
            ) : (
              <i className="ri-add-line text-base"></i>
            )}
          </button>
          <div className="min-w-0">
            <p className="text-foreground-800 text-xs font-bold">{space.name}</p>
            <p className="text-foreground-400 text-[10px] mt-0.5">{space.description}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className="px-2 py-0.5 rounded-full bg-background-200 text-foreground-500 text-[9px] font-medium">{space.format}</span>
              <span className="text-foreground-400 text-[9px]">~{formatCurrency(space.avgMonthlyRevenue)}/mes estimado</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Revenue Share Bar ── */
function RevenueShareBar({ radio, rfw, radioAmount, rfwAmount }: { radio: number; rfw: number; radioAmount: number; rfwAmount: number }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
          <i className="ri-pie-chart-line text-emerald-600 text-lg"></i>
        </div>
        <div>
          <h3 className="text-foreground-800 text-sm font-bold">Revenue Share</h3>
          <p className="text-foreground-400 text-[10px] mt-0.5">
            Distribución del revenue generado por la Red Publicitaria RFW entre tu radio y la plataforma.
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative h-10 rounded-xl overflow-hidden flex mb-3">
        <div
          className="h-full bg-emerald-500 flex items-center justify-center transition-all duration-500"
          style={{ width: `${radio}%` }}
        >
          <span className="text-white text-xs font-black whitespace-nowrap">{radio}% Radio</span>
        </div>
        <div
          className="h-full bg-amber-500 flex items-center justify-center transition-all duration-500"
          style={{ width: `${rfw}%` }}
        >
          <span className="text-white text-xs font-black whitespace-nowrap">{rfw}% RFW</span>
        </div>
      </div>

      <div className="flex items-center justify-between text-[10px]">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-md bg-emerald-500"></span>
          <span className="text-foreground-600 font-medium">Tu radio recibe</span>
          <span className="text-emerald-600 font-black">{formatCurrencyFull(radioAmount)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-foreground-500">RFW recibe</span>
          <span className="text-foreground-500 font-medium">{formatCurrencyFull(rfwAmount)}</span>
          <span className="w-3 h-3 rounded-md bg-amber-500"></span>
        </div>
      </div>
    </div>
  );
}

/* ── History Table ── */
function HistoryTable({ items }: { items: RfwHistoryEntry[] }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
      <div className="px-5 py-3 border-b border-background-200">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-foreground-100 flex items-center justify-center">
            <i className="ri-history-line text-foreground-600 text-base"></i>
          </div>
          <div>
            <h3 className="text-foreground-800 text-sm font-bold">Historial RFW</h3>
            <p className="text-foreground-400 text-[10px]">Últimas campañas gestionadas por la red publicitaria</p>
          </div>
        </div>
      </div>

      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="border-b border-background-200 bg-background-100/50">
              <th className="text-left py-3 px-4 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Fecha</th>
              <th className="text-left py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Anunciante</th>
              <th className="text-left py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Espacio</th>
              <th className="text-right py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Impr.</th>
              <th className="text-right py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Clicks</th>
              <th className="text-right py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">CTR</th>
              <th className="text-right py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Bruto</th>
              <th className="text-right py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Radio</th>
              <th className="text-right py-3 px-4 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">RFW</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b border-background-200 hover:bg-background-50/50 transition-colors">
                <td className="py-3 px-4">
                  <span className="text-foreground-500 text-[10px] whitespace-nowrap">{item.date}</span>
                </td>
                <td className="py-3 px-3">
                  <span className="text-foreground-800 text-xs font-medium">{item.advertiser}</span>
                </td>
                <td className="py-3 px-3">
                  <span className="text-foreground-500 text-xs">{item.space}</span>
                </td>
                <td className="py-3 px-3 text-right">
                  <span className="text-foreground-500 text-xs">{formatNumber(item.impressions)}</span>
                </td>
                <td className="py-3 px-3 text-right">
                  <span className="text-foreground-500 text-xs">{formatNumber(item.clicks)}</span>
                </td>
                <td className="py-3 px-3 text-right">
                  <span className={`text-xs font-medium ${item.ctr >= 4.0 ? 'text-emerald-600' : 'text-foreground-500'}`}>{item.ctr.toFixed(1)}%</span>
                </td>
                <td className="py-3 px-3 text-right">
                  <span className="text-foreground-600 text-xs font-medium">{formatCurrency(item.grossRevenue)}</span>
                </td>
                <td className="py-3 px-3 text-right">
                  <span className="text-emerald-600 text-xs font-bold">{formatCurrency(item.radioShare)}</span>
                </td>
                <td className="py-3 px-4 text-right">
                  <span className="text-amber-600 text-xs font-bold">{formatCurrency(item.rfwShare)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="lg:hidden divide-y divide-background-200">
        {items.map((item) => (
          <div key={item.id} className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-foreground-500 text-[10px]">{item.date}</span>
                <span className="text-foreground-800 text-xs font-semibold">{item.advertiser}</span>
              </div>
              <span className="text-foreground-500 text-[10px]">{item.space}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-[10px] text-foreground-500">
                <span>{formatNumber(item.impressions)} imp.</span>
                <span>{formatNumber(item.clicks)} clicks</span>
                <span>{item.ctr.toFixed(1)}% CTR</span>
              </div>
              <div className="flex items-center gap-2 text-[10px]">
                <span className="text-emerald-600 font-bold">{formatCurrency(item.radioShare)}</span>
                <span className="text-foreground-300">|</span>
                <span className="text-amber-600 font-medium">{formatCurrency(item.rfwShare)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════ */

export default function AdminRfwNetworkPage() {
  const [config, setConfig] = useState<RfwNetworkConfig | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [participation, setParticipation] = useState<boolean>(true);
  const [spaces, setSpaces] = useState<RfwSpaceConfig[]>([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const loadData = useCallback(() => {
    setLoadState('loading');
    setErrorMsg(null);
    simulateLoad(rfwNetworkConfig)
      .then((d) => {
        setConfig(d);
        setParticipation(d.participation);
        setSpaces(d.spaces.map((s) => ({ ...s })));
        setLoadState('ready');
        setDirty(false);
      })
      .catch((err) => {
        setErrorMsg(err.message || 'Error desconocido');
        setLoadState('error');
      });
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleParticipationChange = (v: boolean) => {
    setParticipation(v);
    setDirty(true);
  };

  const handleSpaceToggle = (key: string) => {
    setSpaces((prev) =>
      prev.map((s) => (s.key === key ? { ...s, enabled: !s.enabled } : s))
    );
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await simulateSave();
      setConfig((prev) =>
        prev ? { ...prev, participation, spaces } : prev
      );
      setDirty(false);
      Swal.fire({
        title: 'Configuración guardada',
        text: participation
          ? 'Tu radio sigue participando en la Red Publicitaria RFW.'
          : 'Tu radio dejó de participar en la Red Publicitaria RFW.',
        icon: 'success',
        timer: 2200,
        showConfirmButton: false,
        customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold' },
      });
    } catch {
      Swal.fire({
        title: 'Error al guardar',
        text: 'No se pudo guardar la configuración. Reintentá.',
        icon: 'error',
        customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold' },
      });
    } finally {
      setSaving(false);
    }
  };

  /* ── Loading ── */
  if (loadState === 'loading') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center px-4 h-14">
            <div className="flex items-center gap-2.5 pl-12">
              <div className="w-7 h-7 rounded-lg bg-background-200 animate-pulse"></div>
              <div className="w-32 h-4 rounded-md bg-background-200 animate-pulse"></div>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0">
            <div className="hidden lg:flex items-center h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
              <div className="w-44 h-4 rounded-md bg-background-200 animate-pulse"></div>
            </div>
            <div className="p-4 md:p-6 lg:p-8 pb-8">
              <LoadingSkeleton />
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ── Error ── */
  if (loadState === 'error') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center px-4 h-14">
            <div className="flex items-center gap-2.5 pl-12">
              <div className="w-7 h-7 rounded-lg bg-rose-500 flex items-center justify-center">
                <i className="ri-error-warning-line text-background-50 text-xs"></i>
              </div>
              <h1 className="text-foreground-950 font-bold text-sm">Red Publicitaria RFW</h1>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center py-24">
            <ErrorState message={errorMsg || 'Error desconocido'} onRetry={loadData} />
          </main>
        </div>
      </div>
    );
  }

  const { revenueShare, totalRadioRevenue, totalRfwRevenue, activeCampaigns, totalImpressions, monthLabel, history } = config!;
  const enabledCount = spaces.filter((s) => s.enabled).length;

  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5 pl-12">
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Red Publicitaria RFW</h1>
              <p className="text-foreground-400 text-[9px]">{monthLabel} · {formatCurrency(totalRadioRevenue + totalRfwRevenue)} total</p>
            </div>
          </div>
          <Link to="/" className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors whitespace-nowrap">
            <i className="ri-arrow-left-line text-xs"></i>
            <span className="hidden sm:inline">Volver al sitio</span>
          </Link>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0">
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div>
              <h2 className="text-foreground-950 font-bold text-base">Red Publicitaria RFW</h2>
              <p className="text-foreground-400 text-xs">
                {monthLabel} · {activeCampaigns} campañas activas · Revenue compartido {revenueShare.radio}/{revenueShare.rfw}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {dirty && (
                <span className="text-amber-600 text-[10px] font-medium flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                  Cambios sin guardar
                </span>
              )}
              <Link to="/" className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-external-link-line text-xs"></i>
                Ver sitio
              </Link>
            </div>
          </div>

          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1200px] mx-auto">
            {/* ── Hero ── */}
            <div className="bg-background-50 border border-background-200 rounded-2xl p-5 md:p-6">
              <div className="flex flex-col lg:flex-row lg:items-center gap-5">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 rounded-2xl bg-amber-500 flex items-center justify-center shrink-0">
                      <i className="ri-broadcast-line text-white text-xl"></i>
                    </div>
                    <div>
                      <p className="text-amber-700 text-xs font-bold">Red Publicitaria RFW</p>
                      <p className="text-foreground-800 text-2xl md:text-3xl font-black">{formatCurrencyFull(totalRadioRevenue + totalRfwRevenue)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold ${participation ? 'bg-amber-50 text-amber-700' : 'bg-foreground-100 text-foreground-400'}`}>
                      {participation ? (
                        <>
                          <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                          Participando
                        </>
                      ) : (
                        <>
                          <i className="ri-pause-circle-line text-sm"></i>
                          No participa
                        </>
                      )}
                    </span>
                    <span className="text-foreground-400 text-xs">{enabledCount} de {spaces.length} espacios habilitados</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 lg:w-[380px] shrink-0">
                  <div className="bg-emerald-50 rounded-xl p-3 text-center">
                    <p className="text-emerald-700 text-lg font-black">{formatCurrency(totalRadioRevenue)}</p>
                    <p className="text-emerald-600 text-[9px] font-medium">Tu radio (60%)</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-3 text-center">
                    <p className="text-amber-700 text-lg font-black">{formatCurrency(totalRfwRevenue)}</p>
                    <p className="text-amber-600 text-[9px] font-medium">RFW (40%)</p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-3 text-center">
                    <p className="text-foreground-800 text-lg font-black">{formatNumber(totalImpressions)}</p>
                    <p className="text-foreground-400 text-[9px] font-medium">Impresiones</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ── 1. Participación ── */}
            <ParticipationToggle active={participation} onChange={handleParticipationChange} saving={saving} />

            {/* ── 2. Espacios disponibles ── */}
            <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center shrink-0">
                  <i className="ri-layout-grid-line text-sky-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="text-foreground-800 text-sm font-bold">Espacios disponibles</h3>
                  <p className="text-foreground-400 text-[10px] mt-0.5">
                    Seleccioná qué espacios ponés a disposición de la red RFW.{' '}
                    {!participation && (
                      <span className="text-rose-500 font-medium">Activá la participación primero.</span>
                    )}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {spaces.map((space) => (
                  <SpaceCard
                    key={space.key}
                    space={space}
                    disabled={!participation}
                    onToggle={handleSpaceToggle}
                  />
                ))}
              </div>
            </div>

            {/* ── 3. Revenue Share ── */}
            <RevenueShareBar
              radio={revenueShare.radio}
              rfw={revenueShare.rfw}
              radioAmount={totalRadioRevenue}
              rfwAmount={totalRfwRevenue}
            />

            {/* ── 4. Historial ── */}
            <HistoryTable items={history} />

            {/* ── Save button (fixed) ── */}
            {dirty && (
              <div className="flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-amber-500 text-white text-sm font-bold hover:bg-amber-600 transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {saving ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base"></i>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line text-base"></i>
                      Guardar configuración
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </main>
      </div>


    </div>
  );
}